import { useNavigate } from 'react-router-dom'

import {
  Box,
  Colors,
  InfoDot,
  ProgressBar,
  Text,
  Tooltip,
  useToggle,
} from '@factorialco/deprecated-design-system'

import { useNavigation } from 'modules/core/hooks/useNavigation'
import i18n from 'modules/core/lib/i18n'

import { IncidenceStatField } from 'modules/bookkeepers/store/collections/incidenceStats'
import { typeFilterLabelTranslations } from 'modules/payroll_bundle/lib/Incidences/dynamicTranslations'
import { INCIDENCE_TAB_ID } from 'modules/payroll_bundle/pages/IncidencesPage/components/_ProIncidencePage'

export function calculateWidth(total: number, highestTotal: number): number {
  const MAX_PROGRESSBAR_WIDTH = 150
  return (total / highestTotal) * MAX_PROGRESSBAR_WIDTH
}

export type IncidenceStatFieldWithOthers = IncidenceStatField | 'others'
export interface IncidenceToShow {
  name: IncidenceStatFieldWithOthers
  sent: number | undefined
  pending: number | undefined
  id: number
  showTooltip: boolean
}

interface IncidenceTooltipContentProps {
  name: string
  sentIncidences: number
  pendingIncidence: number
  totalIncidences: number
}

const IncidenceTooltipContent = ({
  name,
  sentIncidences,
  pendingIncidence,
  totalIncidences,
}: IncidenceTooltipContentProps) => {
  return (
    <Box width="s300" padding="s6" gap="s8">
      <Box flexDirection="row" paddingBottom="s16" paddingRight="s2">
        <Text.Normal>
          {name} ({totalIncidences})
        </Text.Normal>
      </Box>
      <Box flexDirection="row" justifyContent="spaceBetween" paddingBottom="s8">
        <Box flexDirection="row" gap="s8" alignItems="center">
          <InfoDot size="medium" color="viridian1000" />
          <Text.Normal>
            {i18n.t('payroll_bundle.payroll_dashboard.incidences_summary.sent')}
          </Text.Normal>
        </Box>
        <Text.Normal>{sentIncidences}</Text.Normal>
      </Box>
      <Box flexDirection="row" justifyContent="spaceBetween">
        <Box flexDirection="row" gap="s8" alignItems="center">
          <InfoDot size="medium" color="viridian200" />
          <Text.Normal>
            {i18n.t('payroll_bundle.payroll_dashboard.incidences_summary.pending')}
          </Text.Normal>
        </Box>
        <Text.Normal>{pendingIncidence}</Text.Normal>
      </Box>
    </Box>
  )
}

interface StatItemProps {
  incidence: IncidenceToShow
  progressbarWidth: number
  barColor?: Colors
  progressColor?: Colors
}

export const StatItem = ({
  incidence,
  progressbarWidth,
  barColor = 'viridian200',
  progressColor = 'viridian1000',
}: StatItemProps) => {
  const navigate = useNavigate()
  const { navigation } = useNavigation()
  const [open, toggle] = useToggle(false)
  const sentIncidence = incidence.sent || 0
  const pendingIncidence = incidence.pending || 0
  const totalIncidences = pendingIncidence + sentIncidence
  const name = typeFilterLabelTranslations(incidence.name)

  return (
    <Box
      width="full"
      flexDirection="row"
      justifyContent="spaceBetween"
      cursor="pointer"
      onClick={() =>
        navigate({
          pathname: navigation.payrollBundle.incidences.fullPath(),
          search: `?tab=${INCIDENCE_TAB_ID}&type=${incidence.name}`,
        })
      }
    >
      <Tooltip.ItemList
        attachment="bottom center"
        open={open}
        size="auto"
        toggle={toggle}
        backgroundColor="grey800"
      >
        <Box gap="s4">
          <Text.Normal size="200" color={'grey800'} weight={'normal'} ellipsis>
            {name}
          </Text.Normal>
          <Box style={{ width: `${progressbarWidth}px` }}>
            <ProgressBar
              barColor={barColor}
              progressColor={progressColor}
              amount={sentIncidence}
              total={totalIncidences}
              size="small"
            />
          </Box>
        </Box>
        <IncidenceTooltipContent
          name={name}
          sentIncidences={sentIncidence}
          pendingIncidence={pendingIncidence}
          totalIncidences={totalIncidences}
        />
      </Tooltip.ItemList>
      <Box>
        <Text.Normal size="200" color={'grey800'} weight={'normal'}>
          {totalIncidences}
        </Text.Normal>
      </Box>
    </Box>
  )
}
